import type { EChartsOption } from 'echarts';
import type { PositiveVsNegativeDataPoint, PositiveVsNegativeChartConfig } from '../types';
import { buildAnimationConfig } from '../shared/animation';
import { buildCategoryAxis, buildValueAxis } from '../shared/axis';
import { buildDateAxisLabels } from '../shared/dateAxisLabels';
import { buildGridConfig } from '../shared/grid';
import { buildTooltipConfig } from '../shared/tooltip';
import { buildPositiveVsNegativeSeries } from './series';

// The combined/Net Worth series renders as a line, so its legend key is a solid 12x2 bar centered
// in the 12x12 icon box (Figma node 6579-28002, "Legend Swatch 4"), not a filled square.
const LINE_LEGEND_ICON = 'path://M0,5 L12,5 L12,7 L0,7 Z';

const buildTooltipFormatter =
  (data: PositiveVsNegativeDataPoint[], config: PositiveVsNegativeChartConfig) =>
  (params: unknown): string => {
    const {
      theme,
      privacyMask,
      formatAmount,
      hideCents = false,
      positiveLabel = 'Income',
      negativeLabel = 'Expenses',
      combinedLabel = 'Net Income',
      positiveColor = theme.colors[0],
      negativeColor = theme.colors[3],
      combinedColor = theme.colors[1],
    } = config;

    const paramArray = Array.isArray(params) ? params : [params];
    if (!paramArray.length) return '';

    const firstParam = paramArray[0] as { dataIndex?: number; name?: string } | null;
    const dataIndex = firstParam?.dataIndex;
    const category = firstParam?.name ?? '';
    const row = typeof dataIndex === 'number' ? data[dataIndex] : undefined;
    if (!row) return '';
    const year = row.startOn?.slice(0, 4);
    const header = year && !category.includes(year) ? `${category}, ${year}` : category;

    const fmtValue = (val: number): string =>
      String(privacyMask(formatAmount(val, 'USD', hideCents ? '0,0' : '0,0.00')));

    let rowsHtml = [
      { label: positiveLabel, value: row.positiveTotal, color: positiveColor },
      { label: negativeLabel, value: row.negativeTotal, color: negativeColor },
    ]
      .map(
        ({ label, value, color }) =>
          `<div style="display:grid;grid-template-columns:10px 1fr auto;gap:6px;align-items:center;margin-top:2px;">
            <div style="width:10px;height:10px;background:${color};border-radius:2px;flex-shrink:0;"></div>
            <span style="color:${theme.textSecondaryColor};font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${label}">${label}</span>
            <span style="color:${theme.textColor};font-size:12px;font-weight:600;">${fmtValue(value)}</span>
          </div>`,
      )
      .join('');

    rowsHtml += `<div style="height:1px;background:${theme.dividerColor};margin:5px 0;"></div>`;
    rowsHtml += `<div style="display:grid;grid-template-columns:10px 1fr auto;gap:6px;align-items:center;margin-top:2px;">
      <div style="width:10px;height:10px;background:${combinedColor};border-radius:50%;flex-shrink:0;"></div>
      <span style="color:${theme.textSecondaryColor};font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${combinedLabel}">${combinedLabel}</span>
      <span style="color:${theme.textColor};font-size:12px;font-weight:600;">${fmtValue(row.combinedTotal)}</span>
    </div>`;

    return `<div style="padding:8px;">
      <div style="color:${theme.textSecondaryColor};font-size:12px;margin-bottom:4px;">${header}</div>
      ${rowsHtml}
    </div>`;
  };

export const buildPositiveVsNegativeChartOption = (
  data: PositiveVsNegativeDataPoint[],
  config: PositiveVsNegativeChartConfig,
): EChartsOption => {
  const {
    theme,
    animated = true,
    hideLegend = false,
    positiveLabel = 'Income',
    negativeLabel = 'Expenses',
    combinedLabel = 'Net Income',
    positiveColor = theme.colors[0],
    negativeColor = theme.colors[3],
    combinedColor = theme.colors[1],
  } = config;

  const categories = data.map((d) => d.category);
  const currencyMaxMagnitude = data.length
    ? Math.max(
        ...data
          .flatMap(({ positiveTotal, negativeTotal, combinedTotal }) => [positiveTotal, negativeTotal, combinedTotal])
          .map(Math.abs),
      )
    : 0;
  // This mixed bar/line chart bypasses the generic builders' zero-only guard. Pin its empty
  // report range so fractional ticks do not collapse into repeated whole-dollar labels.
  const hasZeroOnlyValues =
    data.length > 0 &&
    data.every(
      ({ positiveTotal, negativeTotal, combinedTotal }) =>
        positiveTotal === 0 && negativeTotal === 0 && combinedTotal === 0,
    );
  const axisLabelOverrides = buildDateAxisLabels(data, {
    range: config.dateRange,
    timestep: config.timestep,
  });

  return {
    ...buildAnimationConfig(animated),
    textStyle: { fontFamily: theme.fontFamily },
    color: [positiveColor, negativeColor, combinedColor],
    grid: buildGridConfig({ withLegend: !hideLegend }),
    legend: hideLegend
      ? { show: false }
      : {
          show: true,
          bottom: 0,
          itemWidth: 12,
          itemHeight: 12,
          // Matches the Reports 2026 "Net Worth" card legend spec (Figma node 6579-28002): 24px
          // between entries.
          itemGap: 24,
          textStyle: {
            color: theme.textColor,
            fontSize: theme.fontSize,
            // Matches the Figma dev-mode label spec: max-width 152px, single-line ellipsis.
            width: 152,
            overflow: 'truncate',
          },
          data: [
            // Solid to match the bars' own opacity — unlike the Figma area-chart reference this
            // spec came from, positiveVsNegative's Assets/Liabilities render as solid bars, so a
            // tinted swatch would read as a different (pastel) color than the bar itself.
            { name: positiveLabel, icon: 'roundRect' },
            { name: negativeLabel, icon: 'roundRect' },
            { name: combinedLabel, icon: LINE_LEGEND_ICON },
          ],
        },
    tooltip: {
      ...buildTooltipConfig(theme),
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const, shadowStyle: { opacity: 0 } },
      formatter: buildTooltipFormatter(data, config),
    },
    xAxis: buildCategoryAxis(categories, theme, undefined, undefined, axisLabelOverrides),
    yAxis: {
      ...buildValueAxis({
        theme,
        formatAmount: config.formatAmount,
        privacyMask: config.privacyMask,
        useNumberPrefix: config.useNumberPrefix,
        currencyMaxMagnitude,
        splitNumber: hasZeroOnlyValues ? 1 : config.splitNumber,
        ...(hasZeroOnlyValues && { min: 0, max: 1 }),
      }),
      splitLine: { show: false },
    },
    series: buildPositiveVsNegativeSeries(data, config),
  };
};
